import { DatePicker, Form, Input, Select, TimePicker } from 'antd';
import type { UseOutgoingCallsUploadPopupReturn } from '../../../hooks/features/useOutgoingCallsUploadPopup/useOutgoingCallsUploadPopup';
import type { ErrorState } from './types';
import styles from './style.module.css';
import {
  getCampaignEndDisabledTime,
  isBeforeCampaignEndDate,
  isBeforeMinimumCampaignStartDate,
} from '../../../utils/campaignDateTimeRestrictions';

interface ScenarioFieldsProps {
  readonly reportData: UseOutgoingCallsUploadPopupReturn;
  readonly errors: ErrorState;
  readonly handleFocus: () => void;
}

export const OutgoingCallsUploadScenarioFields = ({
  reportData,
  errors,
  handleFocus,
}: ScenarioFieldsProps) => {
  if (!reportData.taskType) return null;

  const showCampaignName =
    reportData.scenarioGroup === 'dispensarization' ||
    reportData.scenarioGroup === 'form';
  const showStartDate =
    reportData.scenarioGroup === 'dispensarization' ||
    reportData.scenarioGroup === 'form';
  const showEndDate = reportData.scenarioGroup === 'dispensarization';
  const showTemplate = reportData.scenarioGroup === 'form';

  return (
    <>
      {showCampaignName && (
        <Form.Item
          label={
            <span>
              Название кампании <span className={styles.requiredMark}>*</span>
            </span>
          }
          validateStatus={errors.name?.validateStatus}
          help={errors.name?.help}
        >
          <Input
            value={reportData.name}
            onChange={(event) => reportData.onNameChange(event.target.value)}
            onFocus={handleFocus}
            placeholder="Введите название кампании"
          />
        </Form.Item>
      )}

      {showStartDate && showEndDate ? (
        <div className={styles.dateTimeTwoColumn}>
          <Form.Item
            className={styles.dateTimeField}
            label={
              <span>
                Дата/время начала
                <span className={styles.requiredMark}> *</span>
              </span>
            }
            validateStatus={errors.fromDate?.validateStatus}
            help={errors.fromDate?.help}
          >
            <div className={styles.dateTimeRow}>
              <DatePicker
                value={reportData.fromDate}
                onChange={reportData.onFromDateChange}
                onFocus={handleFocus}
                format="DD.MM.YYYY"
                disabledDate={isBeforeMinimumCampaignStartDate}
                placeholder="дд.мм.гггг"
              />
              <TimePicker
                value={reportData.fromTime}
                onChange={reportData.onFromTimeChange}
                format="HH:mm"
                onFocus={handleFocus}
                placeholder="--:--"
              />
            </div>
          </Form.Item>

          <Form.Item
            className={styles.dateTimeField}
            label={
              <span>
                Дата/время окончания
                <span className={styles.requiredMark}> *</span>
              </span>
            }
            validateStatus={errors.toDate?.validateStatus}
            help={errors.toDate?.help}
          >
            <div className={styles.dateTimeRow}>
              <DatePicker
                value={reportData.toDate}
                onChange={reportData.onToDateChange}
                onFocus={handleFocus}
                format="DD.MM.YYYY"
                disabledDate={(date) =>
                  isBeforeCampaignEndDate(date, reportData.fromDate)
                }
                placeholder="дд.мм.гггг"
              />
              <TimePicker
                value={reportData.toTime}
                onChange={reportData.onToTimeChange}
                format="HH:mm"
                disabledTime={() =>
                  getCampaignEndDisabledTime(
                    reportData.toDate,
                    reportData.fromDate,
                    reportData.fromTime,
                  )
                }
                hideDisabledOptions
                onFocus={handleFocus}
                placeholder="--:--"
              />
            </div>
          </Form.Item>
        </div>
      ) : (
        showStartDate && (
          <Form.Item
            label={
              <span>
                Дата/время начала
                <span className={styles.requiredMark}> *</span>
              </span>
            }
            validateStatus={errors.fromDate?.validateStatus}
            help={errors.fromDate?.help}
          >
            <div className={styles.dateTimeRow}>
              <DatePicker
                value={reportData.fromDate}
                onChange={reportData.onFromDateChange}
                onFocus={handleFocus}
                format="DD.MM.YYYY"
                disabledDate={isBeforeMinimumCampaignStartDate}
                placeholder="дд.мм.гггг"
              />
              <TimePicker
                value={reportData.fromTime}
                onChange={reportData.onFromTimeChange}
                format="HH:mm"
                onFocus={handleFocus}
                placeholder="--:--"
              />
            </div>
          </Form.Item>
        )
      )}

      {showTemplate && (
        <Form.Item
          label="Шаблон анкеты"
          validateStatus={errors.templateId?.validateStatus}
          help={errors.templateId?.help}
        >
          <Select
            value={reportData.templateId}
            onChange={reportData.onTemplateChange}
            onFocus={handleFocus}
            options={reportData.formTemplates.map((template) => ({
              value: template.id,
              label: template.name,
            }))}
            placeholder="Выберите шаблон анкеты"
          />
        </Form.Item>
      )}
    </>
  );
};
